import { Chip, alpha } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type {
  ILegalRequirementDependencyAction,
  ILegalRequirementDependencyCondition,
  ILegalRequirementDependencyConditionGroup,
  ILegalRequirementDependencyRule,
  IRequirementOption,
  IRuleAction,
  IRuleCondition,
  IRuleConditionGroup,
  LogicalOperator,
  PhaseStatus,
  RuleTargetType,
} from '../../../Interfaces/ILegalDependency';
import type { ILegalPhaseFullObject } from '../../../Interfaces/ILegalPhase';
import type { ILegalRequirementState } from '../../../Interfaces/ILegalRequirementState';
import type { ILegalTransaction } from '../../../Interfaces/ILegalTransaction';

type StatusTone = 'success' | 'info' | 'warning' | 'default';

export const PHASE_STATUS_OPTIONS: { value: PhaseStatus; label: string }[] = [
  { value: 0, label: 'Offen' },
  { value: 1, label: 'Abgeschlossen' },
];

export const getRuleTransactionId = (rule: ILegalRequirementDependencyRule) =>
  rule.LegalTransaction_idLegalTransaction ?? rule.idLegalTransaction ?? null;

export const getConditionGroupRuleId = (
  group: ILegalRequirementDependencyConditionGroup
) => group.LRDRule_idLRDRule ?? group.idLRDRule ?? null;

export const getConditionGroupId = (
  condition: ILegalRequirementDependencyCondition
) =>
  condition.LRDConditionGroup_idLRDConditionGroup ??
  condition.idLRDConditionGroup ??
  null;

export const getConditionTransactionId = (
  condition: ILegalRequirementDependencyCondition
) =>
  condition.LegalTransaction_idLegalTransaction ??
  condition.idLegalTransaction ??
  null;

export const getConditionRequirementStateId = (
  condition: ILegalRequirementDependencyCondition
) =>
  condition.LegalRequirementState_idLegalRequirementState ??
  condition.idLegalRequirementState ??
  null;

export const getConditionRequirementId = (
  condition: ILegalRequirementDependencyCondition
) =>
  condition.LegalRequirement_idLegalRequirement ??
  condition.idLegalRequirement ??
  null;

export const getConditionPhaseId = (
  condition: ILegalRequirementDependencyCondition
) => condition.LegalPhase_idLegalPhase ?? condition.idLegalPhase ?? null;

export const getActionRuleId = (action: ILegalRequirementDependencyAction) =>
  action.LRDRule_idLRDRule ?? action.idLRDRule ?? null;

export const getActionTransactionId = (
  action: ILegalRequirementDependencyAction
) =>
  action.LegalTransaction_idLegalTransaction ??
  action.idLegalTransaction ??
  null;

export const getActionRequirementStateId = (
  action: ILegalRequirementDependencyAction
) =>
  action.TargetLegalRequirementState_idLegalRequirementState ??
  action.idLegalRequirementState ??
  null;

export const getActionRequirementId = (
  action: ILegalRequirementDependencyAction
) =>
  action.LegalRequirement_idLegalRequirement ??
  action.idLegalRequirement ??
  null;

export const getActionPhaseId = (action: ILegalRequirementDependencyAction) =>
  action.LegalPhase_idLegalPhase ?? action.idLegalPhase ?? null;

export const getTransactionLabel = (
  legalTransaction?: ILegalTransaction | null
) => {
  if (!legalTransaction) {
    return 'Vorgang';
  }

  if (legalTransaction.Title && legalTransaction.Title.trim().length > 0) {
    return legalTransaction.Title;
  }

  return `Vorgang #${legalTransaction.idLegalTransaction}`;
};

export const getRuleName = (
  rule?: Pick<ILegalRequirementDependencyRule, 'Name'> | null
) => {
  const name = rule?.Name?.trim();

  return name && name.length > 0 ? name : null;
};

export const buildRuleNameFromTransactionLabels = (
  transactionLabels: string[],
  fallback = 'Neue Regel'
) => {
  if (transactionLabels.length === 0) {
    return fallback;
  }

  if (transactionLabels.length === 1) {
    return transactionLabels[0];
  }

  if (transactionLabels.length === 2) {
    return `${transactionLabels[0]} -> ${transactionLabels[1]}`;
  }

  if (transactionLabels.length === 3) {
    return transactionLabels.join(' · ');
  }

  return `${transactionLabels[0]} · ${transactionLabels[1]} +${
    transactionLabels.length - 2
  }`;
};

export const createCondition = (
  id: string,
  transactionId: number | null = null,
  idLegalRequirement: number | null = null,
  idLegalRequirementState: number | null = null
): IRuleCondition => ({
  id,
  transactionId,
  targetType: 'requirement',
  idLegalRequirement,
  idLegalPhase: null,
  idLegalRequirementState,
  PhaseIsCompleted: null,
});

export const createConditionGroup = (
  id: string,
  condition: IRuleCondition,
  operator: LogicalOperator | null = null
): IRuleConditionGroup => ({
  id,
  operator,
  conditions: [condition],
});

export const normalizeConditionGroups = (
  groups: IRuleConditionGroup[]
): IRuleConditionGroup[] =>
  groups.map((group, index) => ({
    ...group,
    operator: index === 0 ? null : group.operator || 'UND',
  }));

export const createAction = (
  id: string,
  transactionId: number | null = null,
  idLegalRequirement: number | null = null,
  targetStateId: number | null = null
): IRuleAction => ({
  id,
  transactionId,
  targetType: 'requirement',
  idLegalRequirement,
  idLegalPhase: null,
  targetStateId,
  TargetPhaseIsCompleted: null,
});

export const createEmptyBuilderState = (
  transactionId: number | null,
  createId: () => string
) => ({
  conditionGroups: normalizeConditionGroups([
    createConditionGroup(
      createId(),
      createCondition(createId(), transactionId)
    ),
  ]),
  actions: [createAction(createId(), transactionId)],
});

export const getPhaseStatusLabel = (isComplete: boolean): string =>
  isComplete ? 'Abgeschlossen' : 'Offen';

export const flattenTargetOptions = (
  phases: ILegalPhaseFullObject[],
  transactionId: number,
  transactionLabel: string,
  legalRequirementStates: ILegalRequirementState[]
): IRequirementOption[] => {
  const stateMap = new Map(
    legalRequirementStates.map(state => [
      state.idLegalRequirementState,
      state.LegalRequirementState,
    ])
  );

  return [...phases]
    .sort((a, b) => a.Position - b.Position)
    .flatMap(phase => [
      {
        targetType: 'phase' as const,
        idLegalRequirement: null,
        idLegalPhase: phase.idLegalPhase,
        legalTargetLabel: phase.LegalPhase,
        currentStatusLabel: getPhaseStatusLabel(phase.isComplete),
        legalPhaseLabel: phase.LegalPhase,
        transactionId,
        transactionLabel,
      },
      ...[...phase.LegalRequirementArray]
        .sort((a, b) => a.Position - b.Position)
        .map(requirement => ({
          targetType: 'requirement' as const,
          idLegalRequirement: requirement.idLegalRequirement,
          idLegalPhase: phase.idLegalPhase,
          legalTargetLabel: requirement.LegalRequirement,
          currentStatusLabel:
            stateMap.get(requirement.idLegalRequirementState) ||
            requirement.LegalRequirementState ||
            'Unbekannter Status',
          legalPhaseLabel: phase.LegalPhase,
          transactionId,
          transactionLabel,
          hasError: requirement.hasError ?? false,
        })),
    ]);
};

export const getTargetKey = (
  transactionId: number,
  targetType: RuleTargetType,
  targetId: number
) => `${transactionId}-${targetType}-${targetId}`;

export const getTargetTypeLabel = (targetType: RuleTargetType) =>
  targetType === 'requirement' ? 'Maßnahme' : 'Phase';

export const getTargetOptionLabel = (option: IRequirementOption) => {
  if (option.targetType === 'phase') {
    return option.legalTargetLabel;
  }

  return `${option.legalPhaseLabel} / ${option.legalTargetLabel}`;
};

const getStatusTone = (statusLabel?: string | null): StatusTone => {
  const normalized = statusLabel?.trim().toLowerCase() || '';

  if (
    normalized.includes('erledigt') ||
    normalized.includes('abgeschlossen') ||
    normalized.includes('fertig')
  ) {
    return 'success';
  }

  if (
    normalized.includes('pruefung') ||
    normalized.includes('prüfung') ||
    normalized.includes('review')
  ) {
    return 'warning';
  }

  if (
    normalized.includes('offen') ||
    normalized.includes('neu') ||
    normalized.includes('in bearbeitung')
  ) {
    return 'info';
  }

  return 'default';
};

const getStatusSx = (tone: StatusTone) => {
  if (tone === 'success') {
    return {
      bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.12),
      borderColor: (theme: Theme) => alpha(theme.palette.success.main, 0.3),
      color: 'success.dark',
    };
  }

  if (tone === 'warning') {
    return {
      bgcolor: (theme: Theme) => alpha(theme.palette.warning.main, 0.16),
      borderColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.35),
      color: 'warning.dark',
    };
  }

  if (tone === 'info') {
    return {
      bgcolor: (theme: Theme) => alpha(theme.palette.info.main, 0.12),
      borderColor: (theme: Theme) => alpha(theme.palette.info.main, 0.3),
      color: 'info.dark',
    };
  }

  return {
    bgcolor: (theme: Theme) => alpha(theme.palette.grey[500], 0.08),
    borderColor: (theme: Theme) => alpha(theme.palette.grey[500], 0.2),
    color: 'text.secondary',
  };
};

export const getStatusFieldSx = (statusLabel?: string | null) => {
  const tone = getStatusTone(statusLabel);

  if (tone === 'default') {
    return undefined;
  }

  return {
    '& .MuiOutlinedInput-root': {
      bgcolor: (theme: Theme) => {
        if (tone === 'success') {
          return alpha(theme.palette.success.main, 0.08);
        }

        if (tone === 'warning') {
          return alpha(theme.palette.warning.main, 0.1);
        }

        return alpha(theme.palette.info.main, 0.08);
      },
    },
  };
};

export const renderStatusChip = (
  statusLabel?: string | null,
  size: 'small' | 'medium' = 'small'
) => (
  <Chip
    label={statusLabel || 'Unbekannter Status'}
    size={size}
    sx={{
      border: '1px solid',
      fontWeight: 600,
      ...getStatusSx(getStatusTone(statusLabel)),
    }}
    variant='outlined'
  />
);
